import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const avatarVariants = cva(
  'inline-flex select-none items-center justify-center overflow-hidden bg-gray-200 font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  {
    variants: {
      size: {
        xs: 'h-6 w-6 text-[10px]',
        sm: 'h-8 w-8 text-xs',
        md: 'h-10 w-10 text-sm',
        lg: 'h-12 w-12 text-base',
        xl: 'h-16 w-16 text-lg',
        '2xl': 'h-24 w-24 text-2xl',
      },
      shape: {
        circle: 'rounded-full',
        square: 'rounded-md',
      },
    },
    defaultVariants: { size: 'md', shape: 'circle' },
  }
)

export interface AvatarProps
  extends
    Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'>,
    VariantProps<typeof avatarVariants> {
  src?: string
  alt?: string
  name?: string
  fallback?: React.ReactNode
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2)
  return parts.map(p => p.charAt(0).toUpperCase()).join('') || '?'
}

export const Avatar = React.forwardRef<HTMLSpanElement, AvatarProps>(
  ({ className, size, shape, src, alt, name, fallback, ...rest }, ref) => {
    const [failed, setFailed] = React.useState(false)
    const showImage = src && !failed
    const content = showImage
      ? null
      : (fallback ?? (name ? initials(name) : '?'))

    return (
      <span
        ref={ref}
        className={cn(avatarVariants({ size, shape, className }))}
        aria-label={alt ?? name}
        role="img"
        {...rest}
      >
        {showImage ? (
          <img
            src={src}
            alt={alt ?? name ?? ''}
            onError={() => setFailed(true)}
            className="h-full w-full object-cover"
          />
        ) : (
          <span aria-hidden="true">{content}</span>
        )}
      </span>
    )
  }
)

Avatar.displayName = 'Avatar'

export { avatarVariants }
